import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Plus, Upload, Star, Trash2, Search, MessageSquare, Phone, Mail, X, Building2, MessageCircle, Sparkles } from "lucide-react";

// ── Contacts directory ───────────────────────────────────────────────────
// A lightweight phonebook for the agent: bulk-import a phone export
// (.vcf / .csv), search/sort the result, star the most-contacted, and
// kick off a WhatsApp / SMS / Email conversation from a single row.
// The "Start conversation" composer opens a drawer that POSTs to the
// existing `/api/comms/send` channel-router, so this page reuses the same
// outgoing path that the inbox composer uses.

export default function ContactsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [composerFor, setComposerFor] = useState(null); // contact obj
  const [showCreate, setShowCreate] = useState(false);
  const [importErr, setImportErr] = useState("");
  const [importToast, setImportToast] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/contacts");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) =>
      [c.full_name, c.organisation, c.role, c.phone, c.email]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [items, search]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportErr("");
    setImportToast(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportToast(r.data);
      await load();
    } catch (err) {
      setImportErr(err?.response?.data?.detail || "Import failed");
    } finally {
      e.target.value = "";
    }
  };

  const toggleStar = async (c) => {
    try {
      const r = await api.post(`/contacts/${c.id}/star`);
      setItems((prev) => prev.map((p) => p.id === c.id ? { ...p, is_starred: r.data.is_starred } : p));
    } catch { /* noop */ }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`Delete ${c.full_name}? This can't be undone.`)) return;
    try {
      await api.delete(`/contacts/${c.id}`);
      setItems((prev) => prev.filter((p) => p.id !== c.id));
    } catch { /* noop */ }
  };

  return (
    <Layout
      title="Contacts"
      subtitle={`${items.length} contact${items.length === 1 ? "" : "s"} · search, import a vCard, start a conversation`}
      right={
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".vcf,.csv" onChange={onUpload} className="hidden" data-testid="contacts-import-input" />
          <button
            onClick={() => fileRef.current?.click()}
            className="pm-btn-secondary h-10 w-10 px-0 sm:h-9 sm:w-auto sm:px-3 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
            data-testid="contacts-import-btn"
            title="Import a phone vCard (.vcf) or Google CSV"
            aria-label="Import contacts"
          >
            <Upload size={12} /> <span className="hidden sm:inline">Import .vcf / .csv</span>
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="pm-btn-primary h-10 w-10 px-0 sm:h-9 sm:w-auto sm:px-3 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
            data-testid="contacts-create-btn"
            aria-label="New contact"
          >
            <Plus size={12} /> <span className="hidden sm:inline">New contact</span>
          </button>
        </div>
      }
    >
      {/* Search bar */}
      <div className="mb-4 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, club, role, phone or email…"
          className="w-full pl-9 pr-4 h-11 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-slate-400"
          data-testid="contacts-search-input"
        />
      </div>

      {/* Import toast */}
      {importToast && (
        <div className="mb-3 text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 flex items-center justify-between" data-testid="contacts-import-toast">
          <span className="text-emerald-800 font-bold flex items-center gap-1.5">
            <Sparkles size={11} /> Imported {importToast.new} new + {importToast.merged} merged contact{importToast.total_parsed === 1 ? "" : "s"}
          </span>
          <button onClick={() => setImportToast(null)} className="text-emerald-700 hover:text-emerald-900"><X size={12} /></button>
        </div>
      )}
      {importErr && (
        <div className="mb-3 text-xs bg-red-50 border border-red-200 rounded-lg p-2.5 text-red-700 font-bold" data-testid="contacts-import-error">
          {importErr}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="pm-card p-10 text-center" data-testid="contacts-empty">
          <div className="text-slate-300 mb-3"><Building2 size={36} className="mx-auto" /></div>
          <div className="font-display text-lg font-bold mb-1">
            {search ? "No matches" : "No contacts yet"}
          </div>
          <div className="text-sm text-slate-500 mb-4">
            {search
              ? "Try a different search term."
              : "Import a phone vCard (export your iPhone/Android contacts as .vcf) or add a contact manually."}
          </div>
          {!search && (
            <button
              onClick={() => fileRef.current?.click()}
              className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold inline-flex items-center gap-1.5"
              data-testid="contacts-empty-import-btn"
            >
              <Upload size={12} /> Import contacts
            </button>
          )}
        </div>
      ) : (
        <div className="pm-card divide-y divide-slate-100" data-testid="contacts-list">
          {filtered.map((c) => (
            <ContactRow
              key={c.id}
              contact={c}
              onStart={() => setComposerFor(c)}
              onStar={() => toggleStar(c)}
              onDelete={() => removeContact(c)}
            />
          ))}
        </div>
      )}

      {composerFor && (
        <ConversationComposer
          contact={composerFor}
          onClose={() => setComposerFor(null)}
        />
      )}
      {showCreate && (
        <CreateContactDrawer
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setItems((prev) => [c, ...prev]); setShowCreate(false); }}
        />
      )}
    </Layout>
  );
}

function ContactRow({ contact, onStart, onStar, onDelete }) {
  const initials = (contact.full_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50/60 transition-colors" data-testid={`contact-row-${contact.id}`}>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center font-display font-black text-xs shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={onStar} className={`shrink-0 transition-colors ${contact.is_starred ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`} data-testid={`contact-star-${contact.id}`} title={contact.is_starred ? "Unstar" : "Star"}>
            <Star size={14} fill={contact.is_starred ? "currentColor" : "none"} />
          </button>
          <span className="font-bold text-slate-900 truncate" data-testid={`contact-name-${contact.id}`}>{contact.full_name}</span>
          {contact.role && <span className="text-[11px] text-slate-500 truncate">· {contact.role}</span>}
        </div>
        <div className="text-[11px] text-slate-500 truncate font-mono-tab mt-0.5">
          {contact.organisation && <span>{contact.organisation}</span>}
          {contact.organisation && contact.phone && <span className="mx-1.5">·</span>}
          {contact.phone && <span>{contact.phone}</span>}
          {(contact.phone || contact.organisation) && contact.email && <span className="mx-1.5">·</span>}
          {contact.email && <span>{contact.email}</span>}
        </div>
      </div>
      <button
        onClick={onStart}
        className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 shrink-0"
        data-testid={`contact-start-conversation-${contact.id}`}
        title="Start a conversation"
      >
        <MessageSquare size={12} /> Start
      </button>
      <button
        onClick={onDelete}
        className="text-slate-400 hover:text-red-600 transition-colors shrink-0"
        data-testid={`contact-delete-${contact.id}`}
        title="Delete contact"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function ConversationComposer({ contact, onClose }) {
  // Channel inference: if the contact has a phone, default to WhatsApp; if
  // only email, default to email. The agent can override via the segment
  // toggle at the top of the drawer.
  const defaultChannel = contact.phone ? "whatsapp" : contact.email ? "outlook" : "whatsapp";
  const [channel, setChannel] = useState(defaultChannel);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState(`Hi ${contact.first_name || contact.full_name.split(" ")[0]}`);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentOk, setSentOk] = useState(false);

  const channels = [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, requires: "phone", enabled: !!contact.phone, hint: "Opens via your connected Twilio WhatsApp sender" },
    { id: "sms",      label: "SMS",      icon: Phone,         requires: "phone", enabled: !!contact.phone, hint: "Sent via your provisioned Twilio number" },
    { id: "outlook",  label: "Email",    icon: Mail,          requires: "email", enabled: !!contact.email, hint: "Sent via your connected mailbox" },
  ];

  const send = async () => {
    if (!body.trim()) { setError("Message body is required"); return; }
    if ((channel === "whatsapp" || channel === "sms") && !contact.phone) { setError("This contact has no phone number"); return; }
    if (channel === "outlook" && !contact.email) { setError("This contact has no email address"); return; }
    setError("");
    setSending(true);
    try {
      await api.post("/comms/send", {
        channel,
        to: channel === "outlook" ? contact.email : contact.phone,
        subject: channel === "outlook" ? subject : undefined,
        body,
      });
      setSentOk(true);
      setTimeout(onClose, 1200);
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed — check your channel is connected in Settings → Integrations");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-stretch justify-end bg-black/50" onClick={onClose} data-testid="composer-overlay">
      <div className="bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">New conversation</div>
              <div className="font-display text-2xl font-black tracking-tight">{contact.full_name}</div>
              {contact.role && <div className="text-[11px] text-slate-500 mt-0.5">{contact.role}{contact.organisation ? ` · ${contact.organisation}` : ""}</div>}
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="composer-close-btn" aria-label="Close"><X size={18} /></button>
          </div>
          {/* Channel segment toggle */}
          <div className="grid grid-cols-3 gap-2" data-testid="composer-channel-toggle">
            {channels.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.id}
                  onClick={() => c.enabled && setChannel(c.id)}
                  disabled={!c.enabled}
                  className={`h-9 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 border transition-colors ${
                    channel === c.id
                      ? "bg-slate-900 text-white border-slate-900"
                      : c.enabled
                        ? "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                        : "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                  }`}
                  data-testid={`composer-channel-${c.id}`}
                  title={c.enabled ? c.hint : `${c.requires} not on file`}
                >
                  <Icon size={11} /> {c.label}
                </button>
              );
            })}
          </div>
          <div className="text-[10px] text-slate-500 mt-1 font-mono-tab">
            To: {channel === "outlook" ? contact.email : contact.phone}
          </div>
        </div>
        <div className="p-6 space-y-3">
          {channel === "outlook" && (
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-slate-400"
              data-testid="composer-subject"
            />
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder={`Type your message to ${contact.first_name || contact.full_name.split(" ")[0]}…`}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-slate-400"
            data-testid="composer-body"
          />
          {error && <div className="text-xs text-red-700 font-bold" data-testid="composer-error">{error}</div>}
          {sentOk && <div className="text-xs text-emerald-700 font-bold" data-testid="composer-sent">Sent ✓</div>}
          <button
            onClick={send}
            disabled={sending || sentOk}
            className="pm-btn-primary w-full h-10 rounded-lg text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="composer-send-btn"
          >
            {sending ? "Sending…" : sentOk ? "Sent ✓" : `Send via ${channels.find((c) => c.id === channel)?.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateContactDrawer({ onClose, onCreated }) {
  const [form, setForm] = useState({ full_name: "", organisation: "", role: "", phone: "", email: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.full_name.trim()) { setError("Name is required"); return; }
    setError("");
    setSaving(true);
    try {
      const r = await api.post("/contacts", form);
      onCreated(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-stretch justify-end bg-black/50" onClick={onClose} data-testid="create-contact-overlay">
      <div className="bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b sticky top-0 bg-white z-10 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">New contact</div>
            <div className="font-display text-2xl font-black tracking-tight">Add to phonebook</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Full name *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="new-contact-name" />
          <Field label="Organisation" value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} placeholder="e.g. Arsenal FC" testid="new-contact-org" />
          <Field label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} placeholder="e.g. Sporting Director" testid="new-contact-role" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+44 …" testid="new-contact-phone" />
          <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="name@club.com" testid="new-contact-email" />
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-slate-400"
              data-testid="new-contact-notes"
            />
          </div>
          {error && <div className="text-xs text-red-700 font-bold">{error}</div>}
          <button
            onClick={submit}
            disabled={saving}
            className="pm-btn-primary w-full h-10 rounded-lg text-sm font-bold disabled:opacity-60"
            data-testid="new-contact-save"
          >
            {saving ? "Saving…" : "Save contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-slate-400"
        data-testid={testid}
      />
    </div>
  );
}
